import { openDB } from 'idb';
import moment from "moment";
import _ from 'lodash';

const defaultVersion = 1;
const expirationField = 'expiresAt';

const helpers = {
  getIndexNameByKeys: (indexKeys) => indexKeys.join('_'),
  getDefaultExpiration: () => moment().add(1, 'week').unix(), // 1 week
  getDB: async (dbName, version = defaultVersion, indexKeysArr = null) => {
    const db = await openDB(dbName, version, {
      upgrade(db, oldVersion, newVersion, transaction, event) {
        if (!db.objectStoreNames.contains(dbName)) {
          db.createObjectStore(dbName, {keyPath: 'id', autoIncrement: true});
        }
        const store = transaction.objectStore(dbName);

        if (indexKeysArr) {
          for (const indexKeys of indexKeysArr) {
            const indexName = helpers.getIndexNameByKeys(indexKeys);
            if (!store.indexNames.contains(indexName)) {
              store.createIndex(indexName, indexKeys, {unique: false});
            }
          }
        }

        if (!store.indexNames.contains(expirationField)) {
          store.createIndex(expirationField, expirationField, {unique: false});
        }
      },
    });
    return db;
  },
  getKeyRangeByFilter: (indexFilter) => {
    if (Array.isArray(indexFilter)) {
      return IDBKeyRange.bound(...indexFilter);
    } else {
      return IDBKeyRange.only(indexFilter);
    }
  },
  getKeysBySingleIndex: async (db, indexKeys, indexFilters) => {
    const indexName = helpers.getIndexNameByKeys(indexKeys);
    const filters = indexFilters.map(filter => helpers.getKeyRangeByFilter(filter));
    const results = await Promise.all(filters.map(filter => db.getAllKeysFromIndex(db.name, indexName, filter)));
    return results.flat();
  },
  getKeysByFilters: async (db, filters) => {
    const keysArr = [];
    for (const filter of filters) {
      const {indexKeys, indexFilters} = filter;
      const keys = await helpers.getKeysBySingleIndex(db, indexKeys, indexFilters);
      keysArr.push(keys);
    }
    // console.debug('[apiCacheEnhancedService] getKeysByFilters results', db.name, keysArr)
    return _.intersection(...keysArr);
  },
  getDataByFilters: async (db, filters) => {
    const keys = await helpers.getKeysByFilters(db, filters);
    const tx = db.transaction(db.name, 'readonly');
    const results = [];
    for (const key of keys) {
      const item = await tx.store.get(key);
      delete item[expirationField];
      results.push(item);
    }
    await tx.done;
    return results;
  },
  saveData: async (db, data) => {
    const defaultExpiration = helpers.getDefaultExpiration();
    const tx = db.transaction(db.name, 'readwrite');
    for (const item of data) {
      await tx.store.put({...item, [expirationField]: defaultExpiration});
    }
    await tx.done;
  },
  clearDataByFilters: async (db, filters) => {
    const keys = await helpers.getKeysByFilters(db, filters);
    // console.debug('[apiCacheEnhancedService] clearDataByFilters', db.name, filters, keys);
    const tx = db.transaction(db.name, 'readwrite');
    for (const key of keys) {
      await tx.store.delete(key);
    }
    await tx.done;
  },
  clearExpiringData: async (db) => {
    const indexKeys = [expirationField];
    const indexFilters = [[0, moment().unix()]];
    await helpers.clearDataByFilters(db, [{indexKeys, indexFilters}]);
  },
};

async function loadCachedData(db, filters, cacheHandler, actionFinishedFlag = {value: false}) {
  try {
    const results = await helpers.getDataByFilters(db, filters);
    // console.debug('[apiCacheEnhancedService] got cache results', db.name, results)
    if (results.length > 0 && !actionFinishedFlag.value) {
      cacheHandler(results);
    }
  } catch (e) {
    console.debug('[apiCacheEnhancedService] error in cache read', db.name, e)
  }
}

async function saveDataInCache(db, filters, results) {
  try {
    await helpers.clearDataByFilters(db, filters);
    await helpers.saveData(db, results);
    // console.debug('[apiCacheEnhancedService] saved results', db.name, results)
    await helpers.clearExpiringData(db);
  } catch (e) {
    console.debug('[apiCacheEnhancedService] error in cache write', db.name, e)
  }
}

const handleInner = async (dbName, filters, action, cacheHandler, version = defaultVersion, skipCache = false) => {
  // console.debug('[apiCacheEnhancedService] handle start', dbName, filters, skipCache, version)
  const db = await helpers.getDB(dbName, version, filters.map(filter => filter.indexKeys));
  const actionFinishedFlag = {value: false};

  if (!skipCache) {
    loadCachedData(db, filters, cacheHandler, actionFinishedFlag);
  }

  const results = await action();
  actionFinishedFlag.value = true;
  saveDataInCache(db, filters, results);

  return results;
}
const handle = async (dbName, indexKeys, indexFilters, action, cacheHandler, version = defaultVersion, skipCache = false) => {
  const filters = [{indexKeys, indexFilters}];
  return handleInner(dbName, filters, action, cacheHandler, version, skipCache);
}

const schedulerStartTimeIndex = ['staff_uid', 'startTime'];
const schedulerEndTimeIndex = ['staff_uid', 'endTime'];
const firstPossibleDate = new Date('1900-01-01');
const lastPossibleDate = new Date('2999-12-31');
const handleSchedulerDriven = async (dbName, staffUids, startDate, endDate, action, cacheHandler, version = defaultVersion, skipCache = false) => {
  const filters = [
    {
      indexKeys: schedulerStartTimeIndex,
      indexFilters: staffUids.map(staffUid => [[staffUid, firstPossibleDate], [staffUid, endDate]])
    },
    {
      indexKeys: schedulerEndTimeIndex,
      indexFilters: staffUids.map(staffUid => [[staffUid, startDate], [staffUid, lastPossibleDate]])
    },
  ];
  return handleInner(dbName, filters, action, cacheHandler, version, skipCache);
}

export default {handle, handleSchedulerDriven};
